
import React, { useEffect, useState } from 'react';
import { Card, Button } from '../components/UI';
import { useAuth } from '../components/AuthContext';
import { UserRole } from '../types';
import { Navigate } from 'react-router-dom';
import { Download, FileText, Info, ShieldCheck } from 'lucide-react';
import { API_BASE_URL } from '../config';
import { PageHero } from '../components/PageHero';

export const TelemetryDownload: React.FC = () => {
    const { user } = useAuth();
    const [files, setFiles] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);

    if (!user || (!user.roles.includes(UserRole.ADMIN) && !user.roles.includes(UserRole.WEBADMIN))) {
        return <Navigate to="/" />;
    }

    const loadList = async () => {
        try {
            const baseUrl = API_BASE_URL.endsWith('/') ? API_BASE_URL.slice(0, -1) : API_BASE_URL;
            // FIX: Removed /php/
            const res = await fetch(`${baseUrl}/tools.php`);
            const json = await res.json();
            if (json.status === 'success') setFiles(json.data);
        } catch(e) { console.error(e); }
        finally { setLoading(false); }
    };

    useEffect(() => { loadList(); }, []);

    const getFullUrl = (url: string) => {
        const baseUrl = API_BASE_URL === '.' ? '' : API_BASE_URL;
        return baseUrl ? `${baseUrl}/${url}` : url;
    };

    return (
        <div className="mx-auto max-w-7xl space-y-6 animate-fade-in pb-20">
            <PageHero
                eyebrow="SDC Software"
                title="Telemetrie-Tool"
                description="Die freigegebenen Versionen der offiziellen SDC-Software für Rennleitung und Telemetrie-Auswertung."
                icon={Download}
                accent="#3b82f6"
                stats={[
                    { label: 'Versionen', value: files.length },
                    { label: 'UDP-Port', value: '20777' },
                    { label: 'Zugriff', value: 'Intern' },
                ]}
            />

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <Card title="Verfügbare Versionen" className="rounded-[1.75rem] border-white/[0.075] bg-gradient-to-br from-[#171a24] to-[#0e1017] shadow-[0_24px_70px_rgba(0,0,0,0.22)]">
                    <div className="space-y-4">
                        {loading ? <div className="text-center py-4 text-gray-500">Prüfe Server...</div> : null}
                        {files.length === 0 && !loading ? <p className="text-gray-500 italic">Aktuell sind keine Tools zum Download verfügbar.</p> : null}
                        {files.map(f => (
                            <div key={f.name} className="flex flex-col gap-4 rounded-[1.35rem] border border-white/[0.075] bg-black/25 p-5">
                                <div className="flex justify-between items-start">
                                    <div className="flex items-center gap-4">
                                        <div className="w-12 h-12 bg-blue-900/20 text-blue-500 border border-blue-800 rounded-full flex items-center justify-center">
                                            <FileText size={24}/>
                                        </div>
                                        <div>
                                            <div className="font-bold text-white text-lg">{f.name}</div>
                                            <div className="text-xs text-gray-500">{new Date(f.date * 1000).toLocaleDateString()} • {(f.size / 1024 / 1024).toFixed(2)} MB</div>
                                        </div>
                                    </div>
                                    <a href={getFullUrl(f.url)} download className="w-full sm:w-auto">
                                        <Button icon={Download} className="w-full">Download</Button>
                                    </a>
                                </div>
                                {f.description && (
                                    <div className="text-sm text-gray-300 bg-black/20 p-3 rounded border border-gray-700/50 whitespace-pre-wrap">
                                        {f.description}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </Card>

                <div className="space-y-6">
                    <Card title="Installation" className="rounded-[1.75rem] border-white/[0.075] bg-gradient-to-br from-[#171a24] to-[#0e1017] shadow-[0_24px_70px_rgba(0,0,0,0.22)]">
                        <ul className="space-y-4 text-sm text-gray-300">
                            <li className="flex gap-3"><div className="w-6 h-6 rounded-full bg-gray-800 flex items-center justify-center font-bold text-xs shrink-0">1</div> Tool herunterladen & entpacken.</li>
                            <li className="flex gap-3"><div className="w-6 h-6 rounded-full bg-gray-800 flex items-center justify-center font-bold text-xs shrink-0">2</div> UDP-Telemetrie im Spiel aktivieren (Port 20777).</li>
                            <li className="flex gap-3"><div className="w-6 h-6 rounded-full bg-gray-800 flex items-center justify-center font-bold text-xs shrink-0">3</div> Tool starten und mit dem SDC-Server verbinden.</li>
                        </ul>
                    </Card>
                    <div className="flex gap-3 rounded-[1.35rem] border border-blue-500/25 bg-blue-500/10 p-5">
                        <ShieldCheck className="text-blue-500 shrink-0" />
                        <div className="text-xs text-blue-200">Dieses Tool ist nur für die Ligaleitung bestimmt. Die Weitergabe der Lizenz-Keys ist streng untersagt.</div>
                    </div>
                </div>
            </div>
        </div>
    );
};
